/**
 * @module wishlistItemsMgr
 * @category widgets
 * @subcategory plugin_wishlist
 * @description Represents wishlistItemsMgr component with next features:
 * 1. Allow getting items in the wish list
 * 2. Allow setting items from the wish list to the local storage
 * @example <caption>Example of WishlistItemsMgr module usage</caption>
 * import { WishlistItemsMgr } from 'widgets/toolbox/WishlistItemsMgr';
 *
 *  init() {
 *      super.init();
 *      wishlistItemsMgr.isItemInWishlist(this.prefs().pid, this.prefs().productOptions)
 *          .then((state) => {
 *              this.changeStateWishlistButton(state);
 *          })
 *      .catch(error => console.log(error));
 *  }
 */
import { getJSONByUrl } from 'widgets/toolbox/ajax';
import { getCookie } from 'widgets/toolbox/cookie';
import localStorageWrapper from 'widgets/toolbox/localStorageWrapper';
type TWishlistItem = {
    id: string;
    optionId: string;
    selectedValueId: string;
};
type TResponse = {
    wishlistItems: Array<TWishlistItem>;
};

declare const window: ExtendedWindow;

export class WishlistItemsMgr {
    // eslint-disable-next-line no-use-before-define
    static instance: WishlistItemsMgr | undefined;

    loadWishlistItemsPromise: Promise<TResponse> | undefined;

    /**
     * @description Returns instance of WishlistItemsMgr
     * @returns Instance of WishlistItemsMgr
     */
    static getInstance(): WishlistItemsMgr {
        if (WishlistItemsMgr.instance) {
            return WishlistItemsMgr.instance;
        }

        WishlistItemsMgr.instance = new WishlistItemsMgr();

        return WishlistItemsMgr.instance;
    }

    /**
     * @description Check if product in wish list
     * @param productId Target product id
     * @param productOptions Product options
     * @returns Result
     */
    isItemInWishlist(productId: string | number, productOptions?: Array<Record<string, unknown>>): Promise<boolean> {
        return this.getWishlistItems().then((response: TResponse) => {
            let result = false;

            if (!response) {
                return Promise.resolve(result);
            }

            const items = response.wishlistItems;
            // TODO: This is a temporary solution.Implement support few options on product
            const option = productOptions ? productOptions[0] : null;
            const item = items.find(wishlistItem => wishlistItem.id === String(productId));

            if (!item) {
                return Promise.resolve(result);
            }

            const itemOptionId = item.optionId;
            const selectedValueId = item.selectedValueId;

            result = true;

            if (option && itemOptionId && selectedValueId) {
                result = option.id === itemOptionId && option.selectedValueId === selectedValueId;
            }

            return Promise.resolve(result);
        });
    }

    /**
     * @description Checks if wish list contains items
     * @returns Indicates if items in the wish list
     */
    isItemsInWishList(): Promise<boolean> {
        return this.getWishlistItems().then(result => Promise.resolve(result?.wishlistItems?.length > 0));
    }

    /**
     * @description Do call to get items in the wish list from the server
     * @param url URL to get items in the wish list
     * @returns Fetching result promise
     */
    loadWishListItems(url: string): Promise<TResponse> {
        return getJSONByUrl(url)
            .then((result: TResponse) => {
                this.saveWishListItems(result.wishlistItems);

                return Promise.resolve(result);
            })
            .catch(error => {
                // eslint-disable-next-line no-console
                console.log(error);

                return error;
            });
    }

    /**
     * @description Set wish list items to the local storage
     * @param wishlistItems Object contains items in the wish list
     */
    saveWishListItems(wishlistItems: Array<TWishlistItem>) {
        localStorageWrapper.setItem('currentSid', getCookie('sid'));
        localStorageWrapper.setItem('wishlistItems', wishlistItems ? JSON.stringify(wishlistItems) : []);
    }

    /**
     * @description Update local storage with new items in the wish list
     * @param wishlistItems Object contains items in the wish list
     */
    updateWishlistItems(wishlistItems: Array<TWishlistItem>) {
        localStorageWrapper.removeItem('wishlistItems');
        this.saveWishListItems(wishlistItems);
    }

    /**
     * @description Returns fetching result promise
     * @returns Promise result
     */
    getWishlistItems(): Promise<TResponse> {
        return new Promise(resolve => {
            const url = window.sfccData.wishlistActionUrls?.getItems || null;

            if (!url) {
                // eslint-disable-next-line no-promise-executor-return
                return resolve(null as unknown as TResponse);
            }

            const items = JSON.parse(localStorageWrapper.getItem('wishlistItems') || 'null');
            const isSameSession = localStorageWrapper.getItem('currentSid') === getCookie('sid');
            const isSameSite = localStorageWrapper.getItem('currentSite') === window.sfccData.siteData.id;

            if (items && isSameSession && isSameSite) {
                // eslint-disable-next-line no-promise-executor-return
                return resolve({ wishlistItems: items });
            }

            if (this.loadWishlistItemsPromise) {
                // eslint-disable-next-line no-promise-executor-return
                return resolve(this.loadWishlistItemsPromise);
            }

            this.loadWishlistItemsPromise = this.loadWishListItems(url);

            // eslint-disable-next-line no-promise-executor-return
            return resolve(this.loadWishlistItemsPromise);
        });
    }
}
WishlistItemsMgr.instance = undefined;
